import { Button } from '@/components/ui/button';
import { TaskItemCard } from '@/features/contribution-kit';
import { Icon } from '@/lib/icons';
import { cn } from '@/lib/utils';
import type { TranscriptionPhraseCopy } from './transcription-phrase-copy';
import type { PhraseEntry } from './types';

type TranscriptionPhraseBatchListProps = {
  t: Record<string, unknown>;
  copy: TranscriptionPhraseCopy;
  phrases: PhraseEntry[];
  currentIndex: number;
  completedPhrases: Set<string>;
  page: number;
  hasMore: boolean;
  isLoading: boolean;
  onSelectPhrase: (index: number) => void;
  onPrevPage: () => void;
  onNextPage: () => void;
};

/** The phrases of the page: pick one not yet translated, or change page. */
export function TranscriptionPhraseBatchList({
  t,
  copy,
  phrases,
  currentIndex,
  completedPhrases,
  page,
  hasMore,
  isLoading,
  onSelectPhrase,
  onPrevPage,
  onNextPage,
}: TranscriptionPhraseBatchListProps) {
  return (
    <TaskItemCard
      label={copy.batchLabel}
      corner={
        <div className="flex items-center gap-1">
          <Button type="button" variant="ghost" size="icon" onClick={onPrevPage} disabled={page <= 1 || isLoading} aria-label={copy.previousPage} className="size-9 text-muted-foreground">
            <Icon name="chevronLeft" className="size-4" />
          </Button>
          <Button type="button" variant="ghost" size="icon" onClick={onNextPage} disabled={!hasMore || isLoading} aria-label={copy.nextPage} className="size-9 text-muted-foreground">
            <Icon name="chevronRight" className="size-4" />
          </Button>
        </div>
      }
    >
      <ul className="space-y-1.5">
        {phrases.map((phrase, index) => {
          const isCompleted = completedPhrases.has(phrase.id);
          const isCurrent = index === currentIndex;
          return (
            <li key={phrase.id}>
              <button
                type="button"
                onClick={() => {
                  if (!isCompleted) onSelectPhrase(index);
                }}
                aria-current={isCurrent ? 'true' : undefined}
                aria-disabled={isCompleted || undefined}
                className={cn(
                  'flex min-h-11 w-full items-center gap-3 rounded-lg border px-3 py-2 text-left text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                  isCompleted
                    ? 'cursor-default border-success/25 bg-success/[0.05] text-muted-foreground'
                    : isCurrent
                      ? 'border-brand/40 bg-brand/[0.06] text-foreground'
                      : 'border-border/60 bg-surface text-muted-foreground hover:border-brand/30 hover:text-foreground',
                )}
              >
                <span className="shrink-0 text-xs tabular-nums text-faint-foreground">
                  {String(t.phraseNumber ?? '').replace('{n}', String(index + 1))}
                </span>
                <span className={cn('min-w-0 flex-1 truncate', isCompleted && 'line-through')}>{phrase.text}</span>
                {isCompleted ? (
                  <Icon name="checkCircle" className="size-4 shrink-0 text-success" aria-label={copy.stepDone} />
                ) : isCurrent ? (
                  <span className="shrink-0 text-xs font-medium text-brand">{String(t.inProgress ?? '')}</span>
                ) : null}
              </button>
            </li>
          );
        })}
      </ul>
    </TaskItemCard>
  );
}
